// =============================================================
// TopBar — Executive Modern SaaS Navigation Header
// Features:
// 1. Sleek glassmorphism with backdrop blur & soft lighting.
// 2. Interactive breadcrumb track with SVG chevrons & home mark.
// 3. Unified Business Switcher with real-time search & avatars.
// 4. AI Engine quick status badge with direct access to AI config.
// 5. User Profile Pill with avatar gradient, role tag, and rich dropdown.
// =============================================================

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Business, businessApi, adminApi } from '../lib/api';

const STORAGE_KEY_SELECTED_BIZ = 'active_dashboard_business_id';

interface Props {
  title: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; to?: string }>;
  onOpenNavigation?: () => void;
}

export function TopBar({ title, subtitle, breadcrumbs, onOpenNavigation }: Props) {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ businessId?: string }>();
  const currentBusinessId = params.businessId ? parseInt(params.businessId, 10) : undefined;

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loadingBiz, setLoadingBiz] = useState(false);
  const [bizDropdownOpen, setBizDropdownOpen] = useState(false);
  const [bizSearch, setBizSearch] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [aiProvider, setAiProvider] = useState<string>('gemini');

  const [activeBizId, setActiveBizId] = useState<number | null>(() => {
    if (params.businessId) {
      const parsed = parseInt(params.businessId, 10);
      if (!isNaN(parsed)) return parsed;
    }
    const saved = localStorage.getItem(STORAGE_KEY_SELECTED_BIZ);
    return saved ? parseInt(saved, 10) : null;
  });

  const bizRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U'
    : '??';

  // Load AI configuration for quick status indicator
  useEffect(() => {
    if (user?.isSuperAdmin) {
      adminApi
        .getAIConfig()
        .then((cfg) => {
          if (cfg?.provider) setAiProvider(cfg.provider);
        })
        .catch(() => {});
    }
  }, [user?.isSuperAdmin]);

  // Sync business from URL or storage
  useEffect(() => {
    if (params.businessId) {
      const id = parseInt(params.businessId, 10);
      if (!isNaN(id)) {
        setActiveBizId(id);
        localStorage.setItem(STORAGE_KEY_SELECTED_BIZ, String(id));
      }
    } else {
      const saved = localStorage.getItem(STORAGE_KEY_SELECTED_BIZ);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) {
          setActiveBizId(parsed);
        }
      }
    }
  }, [params.businessId, location.pathname]);

  // Listen for active business updates
  useEffect(() => {
    function handleBizChange(e: Event) {
      const customEvent = e as CustomEvent<number>;
      if (customEvent.detail) {
        setActiveBizId(customEvent.detail);
        return;
      }
      const saved = localStorage.getItem(STORAGE_KEY_SELECTED_BIZ);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) setActiveBizId(parsed);
      }
    }

    window.addEventListener('active-business-change', handleBizChange);
    window.addEventListener('storage', handleBizChange);
    return () => {
      window.removeEventListener('active-business-change', handleBizChange);
      window.removeEventListener('storage', handleBizChange);
    };
  }, []);

  // Fetch business list
  useEffect(() => {
    let mounted = true;
    setLoadingBiz(true);
    businessApi
      .list()
      .then((data) => {
        if (!mounted) return;
        setBusinesses(data);

        setActiveBizId((prev) => {
          if (prev && data.some((b) => b.id === prev)) return prev;
          const saved = localStorage.getItem(STORAGE_KEY_SELECTED_BIZ);
          const savedNum = saved ? parseInt(saved, 10) : null;
          const matched = savedNum ? data.find((b) => b.id === savedNum) : null;
          const chosen = matched ? matched.id : data.length > 0 ? data[0].id : null;
          if (chosen) {
            localStorage.setItem(STORAGE_KEY_SELECTED_BIZ, String(chosen));
          }
          return chosen;
        });
      })
      .catch((err) => {
        console.error('Failed to load businesses for switcher', err);
      })
      .finally(() => {
        if (mounted) setLoadingBiz(false);
      });

    return () => {
      mounted = false;
    };
  }, [organization?.id]);

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (bizRef.current && !bizRef.current.contains(e.target as Node)) {
        setBizDropdownOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when business dropdown opens
  useEffect(() => {
    if (bizDropdownOpen) {
      setBizSearch('');
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [bizDropdownOpen]);

  const effectiveBizId = currentBusinessId ?? activeBizId;
  const activeBusiness =
    businesses.find((b) => b.id === effectiveBizId) ??
    (businesses.length > 0 ? businesses[0] : null);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  function handleSelectBusiness(bizId: number) {
    setBizDropdownOpen(false);
    setActiveBizId(bizId);
    localStorage.setItem(STORAGE_KEY_SELECTED_BIZ, String(bizId));
    window.dispatchEvent(new CustomEvent('active-business-change', { detail: bizId }));

    const path = location.pathname;
    const bizRouteMatch = path.match(/^\/businesses\/\d+(\/.*)?$/);

    if (bizRouteMatch) {
      const subPath = bizRouteMatch[1] ?? '/dashboard';
      navigate(`/businesses/${bizId}${subPath}`);
    } else if (path === '/dashboard') {
      // Stay on dashboard - active-business-change refreshes DashboardPage
    } else if (['/keywords', '/reviews', '/competitors', '/reports', '/integrations', '/ai-assistant'].includes(path)) {
      navigate(`/businesses/${bizId}${path}`);
    }
  }

  const filteredBusinesses = businesses.filter((b) => {
    if (!bizSearch.trim()) return true;
    const query = bizSearch.toLowerCase();
    return (
      b.name.toLowerCase().includes(query) ||
      (b.industry && b.industry.toLowerCase().includes(query)) ||
      (b.locations?.[0]?.city && b.locations[0].city.toLowerCase().includes(query))
    );
  });

  return (
    <header className="topbar">
      {/* LEFT SECTION: Mobile Toggle + Breadcrumbs / Title */}
      <div className="topbar-left">
        <button
          type="button"
          className="topbar-menu-btn"
          aria-label="Open navigation"
          onClick={onOpenNavigation}
        >
          <span />
          <span />
          <span />
        </button>

        {breadcrumbs && breadcrumbs.length > 0 ? (
          <nav className="topbar-breadcrumbs" aria-label="Breadcrumb">
            {/* Root Home Icon */}
            <Link to="/dashboard" className="breadcrumb-home-link" title="Dashboard">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </Link>

            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return (
                <div key={`${crumb.label}-${idx}`} className="breadcrumb-segment">
                  <svg className="breadcrumb-separator-svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>

                  {crumb.to && !isLast ? (
                    <Link to={crumb.to} className="breadcrumb-link">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className={`breadcrumb-item ${isLast ? 'active' : ''}`}>
                      {crumb.label}
                    </span>
                  )}
                </div>
              );
            })}
          </nav>
        ) : (
          <div className="topbar-title-block">
            <span className="topbar-title">{title}</span>
            {subtitle && <span className="topbar-subtitle">{subtitle}</span>}
          </div>
        )}
      </div>

      {/* RIGHT CONTROLS: Business Switcher + AI Chip + User Profile */}
      <div className="topbar-right">
        {/* Business Switcher Dropdown */}
        <div className="business-switcher" ref={bizRef}>
          <button
            id="business-switcher-btn"
            className={`business-switcher-btn ${bizDropdownOpen ? 'is-open' : ''}`}
            onClick={() => setBizDropdownOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={bizDropdownOpen}
            type="button"
          >
            {/* Business Logo Mark */}
            <div className="biz-avatar-mark" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </div>

            {/* Business Copy */}
            <div className="biz-content-copy">
              <div className="biz-eyebrow">
                <span className="biz-eyebrow-text">Business</span>
                <span className="biz-status-dot" title="Live Sync Active" />
              </div>
              <div className="biz-name">
                {activeBusiness ? activeBusiness.name : loadingBiz ? 'Loading...' : 'Select Business'}
              </div>
            </div>

            {/* Smooth Rotating Chevron */}
            <svg
              className={`chevron-icon ${bizDropdownOpen ? 'rotate' : ''}`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {/* Business Switcher Dropdown Menu */}
          {bizDropdownOpen && (
            <div className="business-switcher-dropdown" role="menu">
              {/* Dropdown Header */}
              <div className="biz-dropdown-header">
                <div className="biz-dropdown-title-row">
                  <span className="biz-dropdown-title">{organization?.name ?? 'Organization'}</span>
                  <span className="biz-count-pill">{businesses.length} Total</span>
                </div>
                <div className="biz-dropdown-subtitle">Switch active business profile</div>

                {/* Search Bar for Quick Filtering */}
                {businesses.length > 2 && (
                  <div className="biz-search-box">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="biz-search-input"
                      placeholder="Search businesses..."
                      value={bizSearch}
                      onChange={(e) => setBizSearch(e.target.value)}
                    />
                    {bizSearch && (
                      <button
                        type="button"
                        className="biz-search-clear"
                        onClick={() => setBizSearch('')}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Businesses List */}
              <div className="biz-list">
                {filteredBusinesses.length === 0 ? (
                  <div className="biz-dropdown-empty">
                    {bizSearch ? 'No matching businesses found' : 'No businesses registered yet'}
                  </div>
                ) : (
                  filteredBusinesses.map((biz) => {
                    const isSelected = biz.id === activeBusiness?.id;
                    const bizLetter = (biz.name?.[0] || 'B').toUpperCase();
                    return (
                      <button
                        key={biz.id}
                        className={`biz-dropdown-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectBusiness(biz.id)}
                        role="menuitem"
                        type="button"
                      >
                        <div className={`biz-item-icon ${isSelected ? 'active-icon' : ''}`} aria-hidden="true">
                          {bizLetter}
                        </div>
                        <div className="biz-item-details">
                          <div className="biz-item-name">{biz.name}</div>
                          <div className="biz-item-meta">
                            {biz.industry ?? 'General Business'}
                            {biz.locations?.[0]?.city ? ` • ${biz.locations[0].city}` : ''}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="biz-selected-indicator" title="Currently Selected">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Dropdown Action Footer */}
              <div className="biz-dropdown-footer">
                <button
                  id="add-business-btn"
                  className="biz-dropdown-add-btn"
                  onClick={() => {
                    setBizDropdownOpen(false);
                    navigate('/businesses/new');
                  }}
                  role="menuitem"
                  type="button"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  <span>Add New Business</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Super Admin AI Status Pill */}
        {user?.isSuperAdmin && (
          <Link
            to="/admin/ai-config"
            className="topbar-ai-chip"
            title="AI Engine Active — Click to manage Models & Multi-Key Pool"
          >
            <span className="topbar-ai-sparkle">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4L12 2z" />
              </svg>
            </span>
            <span className="topbar-ai-label">AI: {aiProvider.toUpperCase()}</span>
          </Link>
        )}

        {/* User Profile Menu */}
        <div className="user-menu-wrapper" ref={userRef}>
          <button
            id="user-menu-btn"
            className={`topbar-user-btn ${userMenuOpen ? 'is-open' : ''}`}
            onClick={() => setUserMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            type="button"
          >
            <div className="topbar-avatar" aria-hidden="true">
              {initials}
            </div>
            <div className="topbar-user-copy">
              <span className="topbar-user-name">{user?.firstName || 'User'}</span>
              {user?.isSuperAdmin ? (
                <span className="topbar-role-badge">Super Admin</span>
              ) : (
                <span className="topbar-role-badge regular">Admin</span>
              )}
            </div>
            <svg
              className={`chevron-icon ${userMenuOpen ? 'rotate' : ''}`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {/* User Menu Dropdown */}
          {userMenuOpen && (
            <div className="user-menu-dropdown" role="menu">
              {/* User Profile Header Card */}
              <div className="user-menu-header">
                <div className="user-menu-header-avatar">{initials}</div>
                <div className="user-menu-header-details">
                  <div className="user-menu-name">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="user-menu-email">{user?.email}</div>
                  <div className="user-menu-org-tag">
                    {organization?.name ?? 'Organization'}
                  </div>
                </div>
              </div>

              <div className="user-menu-divider" />

              {/* Navigation Options */}
              <button
                className="user-menu-item"
                onClick={() => {
                  navigate('/settings');
                  setUserMenuOpen(false);
                }}
                role="menuitem"
                type="button"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <span>Account Settings</span>
              </button>

              <button
                className="user-menu-item"
                onClick={() => {
                  navigate('/businesses');
                  setUserMenuOpen(false);
                }}
                role="menuitem"
                type="button"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
                <span>All Businesses</span>
              </button>

              {user?.isSuperAdmin && (
                <>
                  <button
                    className="user-menu-item"
                    onClick={() => {
                      navigate('/admin/ai-config');
                      setUserMenuOpen(false);
                    }}
                    role="menuitem"
                    type="button"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4L12 2z" />
                    </svg>
                    <span style={{ fontWeight: 600, color: '#1d4ed8' }}>AI Model & Key Pool</span>
                  </button>

                  <button
                    className="user-menu-item"
                    onClick={() => {
                      navigate('/users');
                      setUserMenuOpen(false);
                    }}
                    role="menuitem"
                    type="button"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span>User Management</span>
                  </button>
                </>
              )}

              <div className="user-menu-divider" />

              {/* Sign Out */}
              <button
                id="logout-btn"
                className="user-menu-item danger"
                onClick={handleLogout}
                role="menuitem"
                type="button"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
